import { useState } from "react";
import { ZoomIn, ZoomOut, RotateCw, Contrast, X, ChevronLeft, ChevronRight, Plus, ArrowLeft, ArrowRight } from "lucide-react";
import { UploadZone } from "./UploadZone";

export function DocumentViewer({ images, names = [], onImages, onRemove, onReorder }) {
  const [page, setPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [contrast, setContrast] = useState(false);
  const [adding, setAdding] = useState(false);

  const safePage = Math.min(page, Math.max(0, images.length - 1));
  const move = (from, to) => {
    if (to < 0 || to >= images.length) return;
    onReorder(from, to);
    setPage(to);
  };

  if (!images.length) {
    return (
      <div className="flex h-full flex-col">
        <PaneHeader title="Document" />
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-md">
            <UploadZone onImages={onImages} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PaneHeader title="Document">
        <div className="flex items-center gap-2">
          {names[safePage] && (
            <span className="hidden max-w-[180px] truncate font-mono text-[11px] text-[var(--ink-3)] sm:inline" title={names[safePage]} data-testid="document-filename">
              {names[safePage]}
            </span>
          )}
          <div className="flex items-center gap-1">
          <ToolBtn onClick={() => setZoom((z) => Math.max(0.4, z - 0.2))} label="uit" testid="zoom-out"><ZoomOut className="h-4 w-4" /></ToolBtn>
          <span className="w-10 text-center font-mono text-xs text-[var(--ink-3)]">{Math.round(zoom * 100)}%</span>
          <ToolBtn onClick={() => setZoom((z) => Math.min(4, z + 0.2))} label="in" testid="zoom-in"><ZoomIn className="h-4 w-4" /></ToolBtn>
          <ToolBtn onClick={() => setRot((r) => (r + 90) % 360)} label="draai" testid="rotate"><RotateCw className="h-4 w-4" /></ToolBtn>
          <ToolBtn onClick={() => setContrast((c) => !c)} label="contrast" active={contrast} testid="contrast"><Contrast className="h-4 w-4" /></ToolBtn>
          </div>
        </div>
      </PaneHeader>

      <div className="relative flex-1 overflow-auto bg-[var(--surface-muted)] p-4 paper-grain">
        <div className="flex min-h-full items-start justify-center">
          <img
            src={images[safePage]}
            alt={`Pagina ${safePage + 1}`}
            data-testid="document-image"
            style={{
              transform: `scale(${zoom}) rotate(${rot}deg)`,
              filter: contrast ? "contrast(1.5) grayscale(0.2) brightness(1.05)" : "none",
              transition: "transform 0.2s ease, filter 0.2s ease",
            }}
            className="max-w-full rounded-md shadow-lg"
          />
        </div>
        <button
          onClick={() => onRemove(safePage)}
          data-testid="remove-page-btn"
          className="absolute right-6 top-6 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur hover:bg-red-600"
          aria-label="Pagina verwijderen"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {images.length > 1 && (
        <div className="flex items-end gap-2 overflow-x-auto border-t border-[var(--border-subtle)] px-3 py-2" data-testid="page-thumbnails">
          {images.map((img, i) => (
            <div key={i} className="shrink-0">
              <button
                onClick={() => setPage(i)}
                data-testid={`page-thumb-${i}`}
                title={names[i] || `Pagina ${i + 1}`}
                className={`relative block h-16 w-12 overflow-hidden rounded-md border-2 transition-colors ${
                  i === safePage ? "border-[var(--brand)]" : "border-[var(--border-subtle)] hover:border-[var(--border-active)]"
                }`}
              >
                <img src={img} alt={`pagina ${i + 1}`} className="h-full w-full object-cover" />
                <span className="absolute bottom-0 right-0 bg-black/60 px-1 text-[10px] font-bold text-white">{i + 1}</span>
              </button>
              <div className="mt-1 flex justify-center gap-1">
                <button disabled={i === 0} onClick={() => move(i, i - 1)} data-testid={`page-move-left-${i}`} title="Naar voren" className="rounded bg-[var(--surface-muted)] p-0.5 text-[var(--ink-2)] transition-colors hover:text-[var(--brand)] disabled:opacity-30"><ArrowLeft className="h-3 w-3" /></button>
                <button disabled={i === images.length - 1} onClick={() => move(i, i + 1)} data-testid={`page-move-right-${i}`} title="Naar achteren" className="rounded bg-[var(--surface-muted)] p-0.5 text-[var(--ink-2)] transition-colors hover:text-[var(--brand)] disabled:opacity-30"><ArrowRight className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between border-t border-[var(--border-subtle)] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <ToolBtn onClick={() => setPage((p) => Math.max(0, p - 1))} label="vorige" disabled={safePage === 0} testid="prev-page"><ChevronLeft className="h-4 w-4" /></ToolBtn>
          <span className="font-mono text-xs text-[var(--ink-2)]">Pagina {safePage + 1} / {images.length}</span>
          <ToolBtn onClick={() => setPage((p) => Math.min(images.length - 1, p + 1))} label="volgende" disabled={safePage === images.length - 1} testid="next-page"><ChevronRight className="h-4 w-4" /></ToolBtn>
        </div>
        {adding ? (
          <div className="w-56"><UploadZone onImages={(urls, nms) => { onImages(urls, nms); setAdding(false); }} compact /></div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            data-testid="add-page-btn"
            className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-medium text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"
          >
            <Plus className="h-3.5 w-3.5" /> Pagina toevoegen
          </button>
        )}
      </div>
    </div>
  );
}

function PaneHeader({ title, children }) {
  return (
    <div className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--surface)] px-4 py-3">
      <span className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-3)]">{title}</span>
      {children}
    </div>
  );
}

function ToolBtn({ children, onClick, label, active, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      data-testid={testid}
      className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:opacity-30 ${
        active ? "bg-[var(--brand)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"
      }`}
    >
      {children}
    </button>
  );
}
